class MobileMenuDrawer extends Drawer {
  constructor() {
    super();
    this.isVisible = false;
    this.transitionTime = 300;
    this.isHiddenClass = 'mobilemenu--hidden';
    this.isVisibleClass = 'mobilemenu--isVisible';
    this.isActiveClass = 'active';
    this.dropdownListTarget = '.mobilemenu__link[aria-expanded]';
    this.dropdownExpandedListTarget = '.mobilemenu__link[aria-expanded="true"]';
    this.tierActiveTarget = 'mobile-menu-tier.' + this.isActiveClass;
    this.linkTarget = '.mobilemenu__link';

    this.mobilemenuEl = this.querySelector('mobile-menu-drawer .drawer__inner');
    this.pageWrapEl = document.querySelector('.mobile-menu-page');

    this.dropdownListEventClick = this.dropdownListEventClick.bind(this);
    this.close = this.close.bind(this);
    this.listenersAdded = false;

    this.initEventListeners();
    this._onResizeDebounced = this._debounce(this.onResizeWindow.bind(this), 150);
    window.addEventListener('resize', this._onResizeDebounced, { passive: true });
  }

  initEventListeners() {
    if (window.innerWidth < 750 && !this.listenersAdded) {
      this.addEventListener('keyup', this.handleKeyUp.bind(this));
      this.querySelector('[id*=-Overlay]').addEventListener('click', this.close);
      this.drawerTagName = this.tagName.toLowerCase();

      this.addEventListener('click', this.dropdownListEventClick);
      this.listenersAdded = true;
    } else if (window.innerWidth >= 750 && this.listenersAdded) {
      this.removeEventListeners();
    }
  }

  removeEventListeners() {
    this.removeEventListener('keyup', this.handleKeyUp.bind(this));
    this.querySelector('[id*=-Overlay]')?.removeEventListener('click', this.close);
    this.removeEventListener('click', this.dropdownListEventClick);
    this.listenersAdded = false;
  }

  onResizeWindow() {
    this.initEventListeners();
  }

  _debounce(fn, delay) {
    let timeout;
    return function(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  connectedCallback() {
    if (window.innerWidth < 750) {
      this.drawerTagName = this.tagName.toLowerCase();
    }
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  handleKeyUp(evt) {
    if (evt.code === 'Escape') this.close();
  }

  dropdownListEventClick(event) {
    const dropdownListEl = event.target.closest(this.dropdownListTarget);
    if (!dropdownListEl) return false;

    const isExpanded = dropdownListEl.getAttribute('aria-expanded') === 'true';
    dropdownListEl.setAttribute('aria-expanded', !isExpanded);
    return true;
  }

  dropdownListCollapseAll() {
    const expandedDropdownListEls = this.mobilemenuEl.querySelectorAll(this.dropdownExpandedListTarget);
    expandedDropdownListEls.forEach(el => el.setAttribute("aria-expanded", "false"));
  }

  nextTier(tierEl) {
    if (!tierEl) return;

    tierEl.classList.add(this.isActiveClass);
    this.attachScrollingEventListener();
    this.setBackButton();
    this.dropdownListCollapseAll();
  }

  previousTier() {
    const activeTiers = this.mobilemenuEl.querySelectorAll(this.tierActiveTarget);
    const lastActiveTier = activeTiers[activeTiers.length - 1];

    if (lastActiveTier) {
      lastActiveTier.classList.remove(this.isActiveClass);
      this.setBackButton();
      this.dropdownListCollapseAll();
    }
  }

  setBackButton() {
    const backButton = this.mobilemenuEl.querySelector('mobile-menu-button-back');
    if (!backButton) return false;

    const isSingleActiveTier = this.mobilemenuEl.querySelectorAll(this.tierActiveTarget).length === 1;
    backButton.classList.toggle(this.isHiddenClass, isSingleActiveTier);
    return true;
  }

  unsetAllActiveTiers() {
    const activeTierEls = this.mobilemenuEl.querySelectorAll(this.tierActiveTarget);
    activeTierEls.forEach(el => el.classList.remove(this.isActiveClass));
  }

  setFirstTier() {
    const tier1El = this.mobilemenuEl.querySelector('#tier-1-0-0');

    if (!tier1El) return;

    if (tier1El.classList.contains(this.isActiveClass)) {
      this.unsetAllActiveTiers();
      this.dropdownListCollapseAll();
    } else {
      tier1El.classList.add(this.isActiveClass);
      this.attachScrollingEventListener();
    }

    this.setBackButton();
  }

  tierLinkEventClick(event) {
    const linkEl = event.target.closest(this.linkTarget);
    if (!linkEl || linkEl.tagName === 'A' || !linkEl.nextElementSibling) return false;

    const nextTierEl = this.mobilemenuEl.querySelector(`#${linkEl.id.replace('-link', '')}`);
    if (nextTierEl) this.nextTier(nextTierEl);

    return true;
  }

  attachScrollingEventListener() {
    const lastActiveTierEl = Array.from(this.mobilemenuEl.querySelectorAll(`.${this.isActiveClass}`)).find(
      el => !el.classList.contains('mobilemenu--watchingScroll')
    );
    if (!lastActiveTierEl) return;

    const lastActiveTierBodyEl = lastActiveTierEl.querySelector('mobile-menu-tier-body');
    if (!lastActiveTierBodyEl) return;

    lastActiveTierBodyEl.addEventListener(
      'scroll',
      () => {
        lastActiveTierEl.classList.toggle('mobilemenu--scrolling', lastActiveTierBodyEl.scrollTop > 0);
      },
      { passive: true }
    );

    lastActiveTierEl.classList.add('mobilemenu--watchingScroll');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    const drawerActiveClasses = document.documentElement.classList.value.split(' ').filter(className => className.includes('--isActive'));

    if (drawerActiveClasses.length <= 2) {
      document.documentElement.classList.remove('drawer--isActive');
    }
    document.documentElement.classList.remove(`${this.drawerTagName}--isActive`);
    document.body.classList.remove('overflow-hidden-submenu');

    this.unstickProductGridTopBar();
    this.dispatchEvent(new CustomEvent(`${this.drawerTagName}:toggle`, { detail: { action: 'close' } }));
  }
}

class MobileMenuDrawerButton extends DrawerButton {
  constructor() {
    super();
  }
}

customElements.define("mobile-menu-drawer", MobileMenuDrawer);
customElements.define("mobile-menu-drawer-button", MobileMenuDrawerButton);


class MenuDrawerImageLinks extends HTMLElement {
  constructor() {
    super();
    this.attempt = 0;
    this.data_id = this.getAttribute('data-id');
    this.target_el = '';

    this.contentTest();
  }

  contentTest() {

    if (this.attempt < 5) {
      this.target_el = document.querySelector('.megamenu__column--image-links[data-id="' + this.data_id + '"]');

      if (this.target_el == null) {
        setTimeout(() => {
          this.contentTest();
        }, 300)
        this.attempt = this.attempt + 1;
      } else {
        this.copyAndInjectContent();
      }
    }
  }

  copyAndInjectContent() {
    const image_block_div = document.createElement("div");

    image_block_div.classList.add('menu-drawer--image-links','grid','grid--2-col');
    image_block_div.innerHTML = this.target_el.innerHTML;

    this.appendChild(image_block_div);
  }
}
customElements.define("menu-drawer-image-links", MenuDrawerImageLinks);


class MenuDrawerLeadLink extends HTMLElement {
  constructor() {
    super();
    this.attempt = 0;
    this.data_id = this.getAttribute('data-id');
    this.target_el = '';

    if ("requestIdleCallback" in window) {
      requestIdleCallback(() => {this.contentTest();});
    } else {

      setTimeout(() => {
        this.contentTest();
      }, 200);
    }
  }

  contentTest() {
    if (this.attempt < 5) {
      this.target_el = document.querySelector('.megamenu-lead-link[data-id="' + this.data_id + '"]');
      if (this.target_el == null) {
        setTimeout(() => {
          this.contentTest();
        }, 1000)
        this.attempt = this.attempt + 1;
      } else {
        this.copyAndInjectContent();
      }
    }
  }

  copyAndInjectContent() {
    const lead_link_block_div = document.createElement("div");

    lead_link_block_div.classList.add('menu-drawer--lead-link');
    lead_link_block_div.innerHTML = this.target_el.innerHTML;

    this.appendChild(lead_link_block_div);
  }
}
customElements.define("menu-drawer-lead-link", MenuDrawerLeadLink);


class MobileMenuDrawerContent extends HTMLElement {
  constructor() {
    super();
    this.template = document.querySelector('#MobileMenuDrawerContent')
  }

  connectedCallback() {
    this.innerHTML = this.template.innerHTML;
  }
}
customElements.define("mobile-menu-drawer-content", MobileMenuDrawerContent);